import { useState } from 'react';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import { HowItWorksCTA } from '@/reports/shared/ReportSection/components';
import {
  AutomobileSafetyRatings,
  CrashTypeSafetyRatingDetailsItems,
} from '@/reports/types/vehicles';
import { Box, Grid, Icons, Select, Stack, Text } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import {
  CrashTypeSafetyRatingDetails,
  GeneralNumericSafetyDetails,
  GeneralStringSafetyDetails,
  StarredRating,
} from './components';

export interface VehicleSafetyRatingsProps {
  /** A list of safety details */
  vehicleSafetyRatingsList: AutomobileSafetyRatings;
  vehicleYMM: string;
  complaintsCount: number;
}

function HowItWorksCTAContent() {
  return (
    <Text
      variant="body2"
      fontSize="0.9rem"
      color={(theme) => theme.palette.grey[800]}
    >
      Safety data availability can vary based on safety testing, historical data
      availability, and reporting. Safety ratings and crash test data are
      sourced from organizations like the NHTSA, but not all vehicles undergo
      immediate testing upon release. Older or less common models may have
      limited historical safety data. If safety information is not available for
      a particular vehicle, we recommend referring to the manufacturer’s website
      or official safety organizations for the latest safety ratings and
      features.
    </Text>
  );
}

/**
 * This component is responsible for showing all safety details for the vehicle. More specific details
 * for a given trim are available by selecting that trim in a Select component.
 */
export const VehicleSafetyRatings: React.FC<VehicleSafetyRatingsProps> = ({
  vehicleSafetyRatingsList,
  vehicleYMM,
  complaintsCount,
}) => {
  const safetyRatingSelectOptions = vehicleSafetyRatingsList.map((item) => ({
    value: item.vehicle_description || '',
    display: item.vehicle_description || '',
  }));

  const { isMobile } = useScreenSize();
  const [currentSafetyRating, setCurrentSafetyRating] = useState(
    vehicleSafetyRatingsList[0]
  );

  function onFilterSelect(selectedOption: string) {
    const vehicleSafetyRating =
      vehicleSafetyRatingsList.find((vehicleSafetyRating) => {
        return vehicleSafetyRating.vehicle_description === selectedOption;
      }) || vehicleSafetyRatingsList[0];
    setCurrentSafetyRating(vehicleSafetyRating);
  }

  const crashTypeSafetyRatingDetailsItems: CrashTypeSafetyRatingDetailsItems = [
    {
      title: 'Front crash',
      items: [
        {
          text: 'Overall',
          type: 'rating',
          value: currentSafetyRating?.front_crash?.overall_rating ?? null,
        },
        {
          text: 'Driver side',
          type: 'rating',
          value: currentSafetyRating?.front_crash?.driver_side_rating ?? null,
        },
        {
          text: 'Passenger side',
          type: 'rating',
          value:
            currentSafetyRating?.front_crash?.passenger_side_rating ?? null,
        },
        {
          text: 'Picture',
          type: 'image',
          value: currentSafetyRating?.front_crash?.picture ?? null,
        },
      ],
    },
    {
      title: 'Side crash',
      items: [
        {
          text: 'Overall',
          type: 'rating',
          value: currentSafetyRating?.side_crash?.overall_rating ?? null,
        },
        {
          text: 'Driver side',
          type: 'rating',
          value: currentSafetyRating?.side_crash?.driver_side_rating ?? null,
        },
        {
          text: 'Passenger side',
          type: 'rating',
          value: currentSafetyRating?.side_crash?.passenger_side_rating ?? null,
        },
        {
          text: 'Picture',
          type: 'image',
          value: currentSafetyRating?.side_crash?.picture ?? null,
        },
      ],
    },
    {
      title: 'Side pole crash',
      items: [
        {
          text: 'Overall',
          type: 'rating',
          value: currentSafetyRating?.side_pole_crash?.overall_rating ?? null,
        },
        {
          text: 'Picture',
          type: 'image',
          value: currentSafetyRating?.side_pole_crash?.picture ?? null,
        },
      ],
    },
    {
      title: 'Rollover',
      items: [
        {
          text: 'Rating',
          type: 'rating',
          value: currentSafetyRating?.rollover?.rating ?? null,
        },
        {
          text: 'Rating 2',
          type: 'rating',
          value: currentSafetyRating?.rollover?.rating_2 ?? null,
        },
        {
          text: 'Possibility',
          type: 'percentage',
          value: currentSafetyRating?.rollover?.possibility ?? 0,
        },
      ],
    },
  ];

  const numericSafetyDetails = [
    {
      count: currentSafetyRating?.recalls_count,
      name: 'recall',
      href: 'vehicle-recalls',
    },
    {
      count: complaintsCount,
      name: 'complaint',
      href: 'vehicle-safety-complaints',
    },
    {
      count: currentSafetyRating?.investigation_count,
      name: 'investigation',
    },
  ].map((safetyDetail) => (
    <GeneralNumericSafetyDetails
      {...safetyDetail}
      key={safetyDetail.name}
      href={safetyDetail.href}
    />
  ));

  const stringSafetyDetails = [
    {
      name: 'Electronic stability control',
      value:
        currentSafetyRating?.nhtsa_electronic_stability_control || undefined,
    },
    {
      name: 'Forwards Collision Warning',
      value: currentSafetyRating?.nhtsa_forward_collision_warning || undefined,
    },
    {
      name: 'Lane Departure Warning',
      value: currentSafetyRating?.nhtsa_lane_departure_warning || undefined,
    },
  ].map((safetyDetail) => (
    <GeneralStringSafetyDetails {...safetyDetail} key={safetyDetail.name} />
  ));

  return (
    <ReportSection
      showTopBar
      title="NHTSA Safety Ratings"
      hasContent={vehicleSafetyRatingsList.length > 0}
      sectionId="vehicle-safety-ratings"
      howItWorks={<HowItWorksCTA content={HowItWorksCTAContent()} />}
      customEmptyText={
        <EmptyVehicleSectionDisclaimer title="NHTSA Safety Ratings" />
      }
    >
      <Text variant="h5" fontWeight="normal">
        Each year, NHTSA crash tests vehicles and provides safety ratings to
        help consumers make smart decisions about safety when purchasing a
        vehicle.
      </Text>
      <Box display="flex">
        <Icons.Tune
          fontSize="small"
          sx={(theme) => ({ marginRight: theme.spacing(1) })}
        />
        <Text variant="h4" mb={0}>
          Select a trim to view specific safety ratings
        </Text>
      </Box>
      <Select
        id="vehicle-safety-trim-selector"
        defaultValue={safetyRatingSelectOptions[0]?.value}
        options={safetyRatingSelectOptions}
        onOptionSelect={onFilterSelect}
      />
      <Stack direction={isMobile ? 'column' : 'row'}>
        <img
          src={currentSafetyRating?.vehicle_picture}
          alt="vehicle"
          width={isMobile ? '100%' : 180}
        />
        <Stack ml={isMobile ? '0' : '20px'}>
          <Text variant="h5" fontWeight="normal">
            {vehicleYMM}
          </Text>
          <Text variant="h3">Overall safety rating</Text>
          <StarredRating rating={currentSafetyRating?.overall_rating} />
        </Stack>
      </Stack>
      <Stack>
        <Stack sx={{ paddingBottom: '1.25rem' }}>{numericSafetyDetails}</Stack>
        <Stack sx={{ marginBottom: '0.75rem' }}>{stringSafetyDetails}</Stack>
      </Stack>
      <Grid container>
        <CrashTypeSafetyRatingDetails
          crashTypeSafetyRatingDetailsItems={crashTypeSafetyRatingDetailsItems}
        />
      </Grid>
    </ReportSection>
  );
};
